import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiFeil } from '../../api/klient';
import { konsollApi, type OverleveringOversikt } from '../../api/konsoll';
import { datoTid } from './ReiseListe';

const FARGE: Record<string, string> = {
  klar_for_bygging: 'bg-slate-900 text-white',
  under_bygging: 'bg-amber-100 text-amber-900',
  klar_for_demo: 'bg-aksent-100 text-aksent-800',
  overlevert: 'bg-aksent-700 text-white',
  erstattet: 'bg-slate-100 text-slate-500',
};

/**
 * Overleveringen (T081): versjonene som er låst, med byggestatus. En ansatt flytter statusen ett steg fram, eller
 * ett steg tilbake. En ny versjon setter den forrige til «erstattet».
 */
export default function OverleveringPanel({ reiseId }: { reiseId: string }) {
  const { t, i18n } = useTranslation();
  const [oversikt, setOversikt] = useState<OverleveringOversikt | null>(null);
  const [lagrer, setLagrer] = useState(false);
  const [feil, setFeil] = useState<string | null>(null);

  const hent = useCallback(
    () =>
      konsollApi
        .overlevering(reiseId)
        .then(setOversikt)
        .catch((e) => setFeil(e instanceof ApiFeil ? e.kode : 'ukjent')),
    [reiseId]
  );

  useEffect(() => {
    hent();
  }, [hent]);

  async function sett(versjon: number, status: string) {
    setFeil(null);
    setLagrer(true);
    try {
      setOversikt(await konsollApi.settOverleveringsstatus(reiseId, versjon, status));
    } catch (e) {
      setFeil(e instanceof ApiFeil ? e.kode : 'ukjent');
      await hent();
    } finally {
      setLagrer(false);
    }
  }

  if (!oversikt && !feil) return <p role="status">{t('konsoll.laster')}</p>;

  return (
    <div className="flex flex-col gap-4">
      {feil && (
        <p role="alert" className="melding-feil">
          {t(`feil.${feil}`, { defaultValue: t('feil.ukjent') })}
        </p>
      )}
      {oversikt?.venter && (
        <p role="status" className="melding-info">
          {t('konsoll.overlevering.venter', { versjon: oversikt.venter })}
        </p>
      )}
      {oversikt && oversikt.versjoner.length === 0 && !oversikt.venter && <p className="text-slate-700">{t('konsoll.overlevering.ingen')}</p>}

      {oversikt && oversikt.versjoner.length > 0 && (
        <ul className="flex flex-col gap-3">
          {oversikt.versjoner.map((v) => (
            <li key={v.versjon} className="kort p-5">
              <p className="mb-1 flex flex-wrap items-center gap-2 font-medium">
                {t('konsoll.overlevering.versjon', { versjon: v.versjon })}
                <span className={'rounded px-2 py-0.5 text-xs font-medium ' + (FARGE[v.status] ?? 'bg-slate-100')}>{t(`konsoll.overlevering.status.${v.status}`)}</span>
              </p>
              <p className="text-sm text-slate-700">{t('konsoll.overlevering.laast', { dato: datoTid(v.laastTidspunkt, i18n.resolvedLanguage), av: v.laastAv })}</p>
              <p className="text-sm text-slate-700">
                {t('konsoll.overlevering.filer', { count: v.filer, antall: v.filer })} · {t('konsoll.overlevering.fingeravtrykk')}{' '}
                <code className="text-xs">{v.fingeravtrykk.slice(0, 12)}</code>
              </p>
              {v.statusEndret && v.statusEndretAv && (
                <p className="text-sm text-slate-600">{t('konsoll.overlevering.endret', { dato: datoTid(v.statusEndret, i18n.resolvedLanguage), av: v.statusEndretAv })}</p>
              )}
              {v.neste.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {v.neste.map((status) => (
                    <button key={status} type="button" disabled={lagrer} onClick={() => sett(v.versjon, status)} className="knapp-sekundaer">
                      {t('konsoll.overlevering.settTil', { status: t(`konsoll.overlevering.status.${status}`) })}
                    </button>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
